<script setup>
import { computed, onMounted, ref } from "vue";
import { centerCropCloudinaryUrl } from "../utils/cloudinary.js";

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
});

const product = ref(null);
const isLoading = ref(true);
const error = ref("");
const selectedImage = ref("");
const quantity = ref(1);
const modifierSelections = ref({});
const modifierError = ref("");
const addedToCart = ref(false);

const galleryImages = computed(() => {
  if (!product.value) {
    return [];
  }

  return product.value.images?.length
    ? product.value.images
    : product.value.image
      ? [product.value.image]
      : [];
});

const selectedModifiers = computed(() => {
  if (!product.value) {
    return [];
  }

  return product.value.modifierGroups.flatMap((group) => {
    const selectedIds = modifierSelections.value[group.id] || [];
    return group.modifiers.filter((modifier) => selectedIds.includes(modifier.id));
  });
});

const configuredPrice = computed(() => {
  if (!product.value?.price && product.value?.price !== 0) {
    return null;
  }

  return product.value.price + selectedModifiers.value.reduce(
    (total, modifier) => total + (modifier.price || 0),
    0
  );
});

const availabilityText = computed(() => {
  if (!product.value?.available) {
    return "Currently sold out";
  }

  if (!product.value.tracksInventory) {
    return "Made to order";
  }

  if (product.value.stock === 1) {
    return "1 available";
  }

  return `${product.value.stock} available`;
});

onMounted(loadProduct);

async function loadProduct() {
  isLoading.value = true;
  error.value = "";

  try {
    const response = await fetch(`/api/products?id=${encodeURIComponent(props.id)}&pageSize=20`, {
      cache: "no-store",
    });
    const body = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(body.error || "Could not load this product.");
    }

    product.value = body.products?.[0] || null;
    if (!product.value) {
      error.value = "This product could not be found.";
      return;
    }

    selectedImage.value = product.value.image || galleryImages.value[0] || "";
    initialiseModifiers();
  } catch (loadError) {
    console.error("Could not load Square product:", loadError);
    error.value = loadError?.message || "Could not load this product.";
  } finally {
    isLoading.value = false;
  }
}

function initialiseModifiers() {
  for (const group of product.value?.modifierGroups || []) {
    modifierSelections.value[group.id] = group.modifiers
      .filter((modifier) => modifier.onByDefault)
      .map((modifier) => modifier.id);
  }
}

function setSingleModifier(groupId, modifierId) {
  modifierSelections.value[groupId] = modifierId ? [modifierId] : [];
  modifierError.value = "";
  addedToCart.value = false;
}

function toggleModifier(group, modifierId) {
  const selected = [...(modifierSelections.value[group.id] || [])];
  const index = selected.indexOf(modifierId);

  if (index >= 0) {
    selected.splice(index, 1);
  } else if (!group.maxSelected || selected.length < group.maxSelected) {
    selected.push(modifierId);
  }

  modifierSelections.value[group.id] = selected;
  modifierError.value = "";
  addedToCart.value = false;
}

function addToCart() {
  const requestedQuantity = Math.max(1, Math.min(99, Number(quantity.value) || 1));
  quantity.value = requestedQuantity;

  for (const group of product.value.modifierGroups || []) {
    const selectedIds = modifierSelections.value[group.id] || [];

    if (selectedIds.length < group.minSelected) {
      modifierError.value = `Choose at least ${group.minSelected} option from ${group.name}.`;
      return;
    }

    if (group.maxSelected && selectedIds.length > group.maxSelected) {
      modifierError.value = `Choose no more than ${group.maxSelected} options from ${group.name}.`;
      return;
    }
  }

  const modifiers = selectedModifiers.value.map((modifier) => ({
    id: modifier.id,
    name: modifier.name,
    price: modifier.price || 0,
  }));
  const modifierIds = modifiers.map((modifier) => modifier.id).sort();
  const key = [product.value.id, ...modifierIds].join(":");
  const cart = loadCart();
  const existing = cart.find((line) => line.key === key);

  if (existing) {
    existing.quantity = Math.min(existing.quantity + requestedQuantity, 99);
  } else {
    cart.push({
      key,
      catalogObjectId: product.value.id,
      title: product.value.title,
      image: product.value.image,
      price: product.value.price,
      currency: product.value.currency,
      quantity: requestedQuantity,
      modifiers,
    });
  }

  localStorage.setItem("bumblefox-square-cart", JSON.stringify(cart));
  window.dispatchEvent(new Event("bumblefox-cart-updated"));
  modifierError.value = "";
  addedToCart.value = true;
}

function loadCart() {
  try {
    const savedCart = JSON.parse(localStorage.getItem("bumblefox-square-cart") || "[]");
    return Array.isArray(savedCart) ? savedCart : [];
  } catch {
    return [];
  }
}

function formatPrice(amount, currency = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency === "GB" ? "GBP" : currency,
    }).format(amount);
  } catch {
    return `£${Number(amount).toFixed(2)}`;
  }
}

function displayImageUrl(url, width = 1200, height = 1200) {
  return centerCropCloudinaryUrl(url, { width, height });
}
</script>

<template>
  <main class="product-page">
    <nav class="breadcrumbs" aria-label="Breadcrumb">
      <router-link to="/">Home</router-link>
      <span aria-hidden="true">/</span>
      <router-link to="/shop">Shop</router-link>
      <template v-if="product">
        <span aria-hidden="true">/</span>
        <span>{{ product.title }}</span>
      </template>
    </nav>

    <p v-if="isLoading" class="product-status">Loading product...</p>

    <div v-else-if="error" class="product-status product-error">
      <p>{{ error }}</p>
      <router-link to="/shop">Return to the shop</router-link>
    </div>

    <article v-else-if="product" class="product-layout">
      <section class="product-gallery" aria-label="Product images">
        <div class="main-image">
          <img v-if="selectedImage" :src="displayImageUrl(selectedImage)" :alt="product.title" />
          <div v-else class="product-image-placeholder">
            <img
              :src="displayImageUrl('https://res.cloudinary.com/dcespjfhu/image/upload/v1780898741/bkzqdkzc6juvkqz5nd0y.avif')"
              alt=""
              @error="$event.currentTarget.hidden = true"
            />
            <span>Photos coming soon</span>
          </div>
        </div>

        <div v-if="galleryImages.length > 1" class="image-thumbnails">
          <button
            v-for="(image, index) in galleryImages"
            :key="image"
            type="button"
              :class="{ active: image === selectedImage }"
              :aria-label="`View product image ${index + 1}`"
              @click="selectedImage = image"
            >
            <img :src="displayImageUrl(image, 160, 160)" alt="" />
          </button>
        </div>
      </section>

      <section class="product-summary">
        <p class="product-category">{{ product.category }}</p>
        <h1>{{ product.title }}</h1>
        <p v-if="product.brand" class="product-brand">By {{ product.brand }}</p>

        <p v-if="configuredPrice !== null" class="product-price">
          {{ formatPrice(configuredPrice, product.currency) }}
        </p>

        <p class="availability" :class="{ unavailable: !product.available }">
          <span aria-hidden="true"></span>
          {{ availabilityText }}
        </p>

        <div v-if="product.description" class="product-description">
          <h2>About this item</h2>
          <p>{{ product.description }}</p>
        </div>

        <div v-if="product.modifierGroups?.length" class="product-options">
          <h2>Choose your options</h2>

          <fieldset v-for="group in product.modifierGroups" :key="group.id">
            <legend>{{ group.name }}</legend>

            <select
              v-if="group.selectionType === 'SINGLE'"
              :value="modifierSelections[group.id]?.[0] || ''"
              @change="setSingleModifier(group.id, $event.target.value)"
            >
              <option v-if="group.minSelected === 0" value="">No option</option>
              <option
                v-for="modifier in group.modifiers"
                :key="modifier.id"
                :value="modifier.id"
              >
                {{ modifier.name }}
                {{ modifier.price ? `(+${formatPrice(modifier.price, modifier.currency)})` : "" }}
              </option>
            </select>

            <label
              v-for="modifier in group.modifiers"
              v-else
              :key="modifier.id"
              class="modifier-choice"
            >
              <input
                type="checkbox"
                :checked="modifierSelections[group.id]?.includes(modifier.id)"
                @change="toggleModifier(group, modifier.id)"
              />
              <span>{{ modifier.name }}</span>
              <span v-if="modifier.price">
                +{{ formatPrice(modifier.price, modifier.currency) }}
              </span>
            </label>
          </fieldset>
        </div>

        <p v-if="modifierError" class="option-error">{{ modifierError }}</p>

        <div class="purchase-row">
          <label class="quantity-control">
            <span>Quantity</span>
            <input v-model.number="quantity" type="number" min="1" max="99" />
          </label>

          <button
            type="button"
            class="add-button"
            :disabled="!product.available || product.price === null"
            @click="addToCart"
          >
            {{ product.available ? "Add to cart" : "Sold out" }}
          </button>
        </div>

        <div v-if="addedToCart" class="added-message" role="status">
          Added to your cart.
          <router-link :to="{ name: 'shop', query: { cart: 'open' } }">View cart</router-link>
        </div>

        <p class="purchase-reassurance">
          Secure checkout with Square. Shipping is confirmed before payment.
        </p>

        <dl class="product-facts">
          <div>
            <dt>Category</dt>
            <dd>{{ product.category }}</dd>
          </div>
          <div v-if="product.variationName && product.variationName.toLowerCase() !== 'regular'">
            <dt>Variation</dt>
            <dd>{{ product.variationName }}</dd>
          </div>
          <div>
            <dt>Availability</dt>
            <dd>{{ availabilityText }}</dd>
          </div>
        </dl>
      </section>
    </article>
  </main>
</template>

<style scoped>
.product-page {
  min-height: 70vh;
  padding: 28px 24px 64px;
  background: #fff8ef;
  color: #2c3e50;
}

.breadcrumbs {
  display: flex;
  gap: 8px;
  max-width: 1160px;
  margin: 0 auto 24px;
  overflow: hidden;
  color: #6b6879;
  font-size: 14px;
  white-space: nowrap;
}

.breadcrumbs a {
  color: #2e2d61;
}

.breadcrumbs span:last-child {
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-status {
  max-width: 1160px;
  margin: 60px auto;
  text-align: center;
}

.product-error {
  color: #8a2f2f;
}

.purchase-reassurance {
  margin: 14px 0 0;
  color: #5d5b70;
  font-size: 0.9rem;
  text-align: center;
}

.product-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(340px, 0.85fr);
  gap: 48px;
  width: 100%;
  max-width: 1160px;
  margin: 0 auto;
}

.product-gallery,
.product-summary {
  min-width: 0;
  max-width: 100%;
}

.main-image {
  display: grid;
  place-items: center;
  width: 100%;
  max-width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border-radius: 8px;
  background: #eee9e3;
  color: #777;
}

.main-image img {
  display: block;
  width: 100%;
  max-width: 100%;
  height: 100%;
  object-fit: contain;
}

.product-image-placeholder {
  position: relative;
  width: 100%;
  max-width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  overflow: hidden;
  color: #fff;
  font-size: 24px;
  font-weight: 800;
  text-align: center;
}

.product-image-placeholder img {
  position: absolute;
  inset: 0;
  object-fit: cover;
  filter: grayscale(1) contrast(0.72) brightness(0.72);
  transform: scale(1.02);
}

.product-image-placeholder::after {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(46, 45, 97, 0.2);
}

.product-image-placeholder span {
  position: relative;
  z-index: 1;
  padding: 12px 18px;
  border-radius: 6px;
  background: rgba(31, 30, 54, 0.72);
}

.image-thumbnails {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  overflow-x: auto;
  padding: 3px;
}

.image-thumbnails button {
  width: 76px;
  height: 76px;
  flex: 0 0 auto;
  padding: 0;
  overflow: hidden;
  border: 2px solid transparent;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.image-thumbnails button.active {
  border-color: #2e2d61;
}

.image-thumbnails img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-summary {
  text-align: left;
}

.product-category {
  margin: 0 0 8px;
  color: #c65073;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
}

.product-summary h1 {
  margin: 0;
  font-size: 34px;
  line-height: 1.15;
}

.product-brand {
  margin: 10px 0;
  color: #6b6879;
}

.product-price {
  margin: 22px 0 12px;
  color: #2e2d61;
  font-size: 27px;
  font-weight: 800;
}

.availability {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 26px;
  color: #28713d;
  font-weight: 700;
}

.availability span {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: currentColor;
}

.availability.unavailable {
  color: #a33131;
}

.product-description {
  padding: 22px 0;
  border-top: 1px solid #ddd6cd;
  border-bottom: 1px solid #ddd6cd;
}

.product-description h2,
.product-options h2 {
  margin: 0 0 10px;
  color: #2e2d61;
  font-size: 18px;
}

.product-description p {
  margin: 0;
  line-height: 1.7;
  white-space: pre-line;
}

.product-options {
  display: grid;
  gap: 16px;
  margin-top: 22px;
}

.product-options fieldset {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  border: 0;
}

.product-options legend {
  margin-bottom: 7px;
  font-weight: 800;
}

.product-options select,
.quantity-control input {
  min-height: 44px;
  border: 1px solid #bdb8c3;
  border-radius: 6px;
  background: #fff;
  padding: 8px 10px;
  font: inherit;
}

.modifier-choice {
  display: flex;
  align-items: center;
  gap: 8px;
}

.modifier-choice span:last-child {
  margin-left: auto;
}

.purchase-row {
  display: flex;
  align-items: end;
  gap: 12px;
  margin-top: 24px;
}

.quantity-control {
  display: grid;
  gap: 5px;
  width: 92px;
  font-size: 13px;
  font-weight: 700;
}

.add-button {
  min-height: 44px;
  flex: 1;
  border: 0;
  border-radius: 7px;
  background: #2e2d61;
  color: #fff;
  padding: 10px 18px;
  font: inherit;
  font-weight: 800;
  cursor: pointer;
}

.add-button:disabled {
  background: #a9a7b4;
  cursor: not-allowed;
}

.option-error {
  color: #a33131;
}

.added-message {
  margin-top: 12px;
  border: 1px solid #8cc89b;
  border-radius: 6px;
  background: #f0fff3;
  padding: 11px;
  color: #225c30;
}

.added-message a {
  margin-left: 5px;
  color: inherit;
  font-weight: 800;
}

.product-facts {
  display: grid;
  margin: 28px 0 0;
  border-top: 1px solid #ddd6cd;
}

.product-facts div {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 15px;
  padding: 11px 0;
  border-bottom: 1px solid #ddd6cd;
}

.product-facts dt {
  color: #6b6879;
}

.product-facts dd {
  margin: 0;
  font-weight: 700;
}

@media (max-width: 780px) {
  .product-page {
    padding: 20px 16px 48px;
  }

  .product-layout {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  .product-summary h1 {
    font-size: 28px;
  }

  .purchase-row {
    align-items: stretch;
    flex-direction: column;
  }

  .quantity-control,
  .purchase-row .add-button {
    width: 100%;
    box-sizing: border-box;
  }
}
</style>
